import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, ShieldCheck, ArrowRight, Linkedin } from "lucide-react";
import Seo from "../components/Seo";
import { publicApi } from "../lib/api";
import NotFound from "./NotFound";

export default function AskCassieReviewer() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    publicApi.askCassieReviewer(slug).then(setR).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <NotFound />;
  if (!r) return <div className="py-32 text-center text-sm text-slate-400">Loading…</div>;

  return (
    <div className="bg-white">
      <Seo
        title={`${r.name} — ${r.role}`}
        description={r.bio || `${r.name}, ${r.role}, reviews the answers in Ask Cassie.`}
        noindex={!r.complete}
      />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-10 pb-20 md:pt-14">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
          <Link to="/" className="hover:text-navy">Home</Link>
          <ChevronRight size={12} />
          <Link to="/ask-cassie" className="hover:text-navy">Ask Cassie</Link>
          <ChevronRight size={12} />
          <span className="text-slate-500">{r.name}</span>
        </nav>

        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-orange mt-8">
          <ShieldCheck size={14} /> Who reviews these answers
        </div>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-6">
          {r.photo && (
            <img
              src={r.photo}
              alt={`${r.name}, ${r.role}`}
              data-testid="reviewer-photo"
              className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl object-cover border border-slate-100 shrink-0"
            />
          )}
          <div>
            <h1 className="font-display font-extrabold text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.1]" data-testid="reviewer-name">
              {r.name}
            </h1>
            <p className="text-slate-500 text-base md:text-lg mt-3">{r.role}</p>
          </div>
        </div>

        {r.remit && (
          <p className="mt-7 text-navy text-base md:text-lg leading-[1.7] font-medium" data-testid="reviewer-remit">
            {r.remit}
          </p>
        )}

        {r.bio ? (
          <div className="mt-8" data-testid="reviewer-bio">
            {r.bio.split("\n\n").filter(Boolean).map((p, i) => (
              <p key={i} className="text-slate-600 leading-[1.85] mt-4 first:mt-0">{p.trim()}</p>
            ))}
          </div>
        ) : (
          <p className="mt-8 rounded-2xl border border-orange/30 bg-orange/5 p-5 text-sm text-slate-600 leading-relaxed" data-testid="reviewer-incomplete">
            This profile is waiting on CASS: the background and credentials are added in the admin, and until then
            the page stays out of Google. Nothing has been written here on anyone's behalf.
          </p>
        )}

        {((r.markers || []).length > 0 || (r.credentials || []).length > 0) && (
          <div className="mt-10" data-testid="reviewer-credentials">
            <h2 className="text-base md:text-lg font-display font-bold text-navy">Experience</h2>
            <ul className="mt-4 space-y-2">
              {((r.markers || []).slice(0, 3).length ? r.markers.slice(0, 3) : r.credentials).map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <ShieldCheck size={14} className="text-teal shrink-0 mt-0.5" /> {c}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <Link
            to="/ask-cassie"
            data-testid="reviewer-library-link"
            className="inline-flex items-center gap-2 text-navy font-bold text-sm hover:text-orange transition-colors"
          >
            {r.reviewed_count > 0
              ? `${r.reviewed_count} answer${r.reviewed_count === 1 ? "" : "s"} reviewed`
              : "Browse the answer library"}{" "}
            <ArrowRight size={15} />
          </Link>
          {r.linkedin && (
            <a
              href={r.linkedin}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-navy text-sm font-semibold transition-colors"
            >
              <Linkedin size={15} /> LinkedIn
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
